import { z } from "zod";

export const policyModes = ["allow", "deny", "require_approval", "conditional"] as const;

export type PolicyMode = (typeof policyModes)[number];

export const conditionFields = [
  "amount",
  "environment",
  "risk_level",
  "customer_tier",
  "action_subtype",
  "risk_class",
  "origin_system",
  "chain_amount_sum"
] as const;

export const conditionOps = ["eq", "neq", "gt", "gte", "lt", "lte", "in", "not_in"] as const;

const conditionValueSchema = z.union([
  z.string(),
  z.number(),
  z.array(z.string()),
  z.array(z.number())
]);

export const conditionSchema = z.object({
  field: z.enum(conditionFields),
  op: z.enum(conditionOps),
  value: conditionValueSchema
});

export type Condition = z.infer<typeof conditionSchema>;

/** Suggested parameter modifications returned when a rule has `then: require_modified_parameters` */
export const policyModificationsSchema = z.object({
  /** Cap the amount to this value (for financial actions) */
  cap_amount: z.number().positive().optional(),
  /** Force the currency to this value */
  force_currency: z.string().min(1).optional(),
  /** Fields that must be provided before re-submitting */
  require_fields: z.array(z.string()).optional()
});

export type PolicyModifications = z.infer<typeof policyModificationsSchema>;

export const policyRuleDecisions = [
  "allow",
  "deny",
  "require_approval",
  "require_modified_parameters"
] as const;

export const policyRuleSchema = z.object({
  when: z.array(conditionSchema).min(1),
  then: z.enum(policyRuleDecisions),
  modifications: policyModificationsSchema.optional(),
  /** Approver group notified when this rule requires human approval */
  approver_group_id: z.string().min(1).optional(),
  /** Optional permit TTL override (seconds) when rule results in allow via approval path */
  permit_ttl_seconds: z.number().int().positive().optional()
});

export type PolicyRule = z.infer<typeof policyRuleSchema>;

export const policyDecisionSchema = z.enum(["allow", "deny", "require_approval"]);

export const actionPolicySchema = z.object({
  mode: z.enum(policyModes),
  rules: z.array(policyRuleSchema).optional(),
  default_decision: policyDecisionSchema.optional(),
  /** When mode is "conditional" and risk_class is "low", auto-allow without human review */
  auto_allow_low_risk: z.boolean().optional()
});

export type ActionPolicy = z.infer<typeof actionPolicySchema>;
